import {
  BufferGeometry,
  BufferAttribute,
  MeshLambertMaterial,
  TextureLoader,
  Mesh,
  Group,
  CylinderGeometry,
  TorusGeometry,
  SphereGeometry
} from 'three'
import { setOpacity } from '../utilities/utilities'
import ShipTexture from '../textures/enemyTexture.jpg'

const BODY_VERTICES = [
  [0, 0, -0.5], // 0: back middle.
  [0, 0, 0.5], // 1: Front middle.
  [0.7, 0.45, 0], // 2: top right.
  [0.7, -0.45, 0], // 3: Bottom right.
  [-0.7, 0.45, 0], // 4: Top left.
  [-0.7, -0.45, 0], // 5: Bottom left.
  [0, 0.825, 0], // 6: top
  [0, -0.825, 0] // 7: bottom
]

const TEXTURE_VERTICES = [
  [0.5, 0.5],
  [0.5, 0.5],
  [1, 0.75],
  [1, 0.25],
  [0, 0.75],
  [0, 0.25],
  [0.5, 1],
  [0.5, 0]
]

const COLOR_PALLET = [
  [1, 0.1, 0.1], // 0: red.
  [0.5, 0.1, 0], // 1: Light green.
  [0.6, 0.2, 0], // 2: Bright yellow-ish.
  [0.5, 0.2, 0], // 3. Medium green.
  [0.3, 0.2, 0], // 4: Light cyan-ish.
  [0.5, 0.2, 0] // 5: Dark orange-ish.
]

const createShipBody = () => {
  const geometry = new BufferGeometry()

  const vertices = new Float32Array([
    ...BODY_VERTICES[0],
    ...BODY_VERTICES[1],
    ...BODY_VERTICES[2],
    ...BODY_VERTICES[3],
    ...BODY_VERTICES[4],
    ...BODY_VERTICES[5],
    ...BODY_VERTICES[6],
    ...BODY_VERTICES[7]
  ])

  geometry.setAttribute('position', new BufferAttribute(vertices, 3))

  const colors = new Float32Array([
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0],
    ...COLOR_PALLET[0]
  ])

  geometry.setAttribute('color', new BufferAttribute(colors, 3))

  const textureCoords = new Float32Array([
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[6],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[7],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[7],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[1],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[6],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[4],
    ...TEXTURE_VERTICES[6],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[6],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[2],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[3],
    ...TEXTURE_VERTICES[7],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[7],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[0],
    ...TEXTURE_VERTICES[5],
    ...TEXTURE_VERTICES[4]
  ])

  geometry.setAttribute('uv', new BufferAttribute(textureCoords, 2))

  // prettier-ignore
  geometry.setIndex([
    1,6,4, // Front 10 o clock
    1,4,5, // Front 8 o clock
    1,5,7, // Front 7 o clock
    1,7,3, // Front 5 o clock
    1,3,2, // Front 3 o clock
    1,2,6, // Front 1 o clock
    0,4,6, // Back 1 o clock
    0,6,2, // Back 10 o clock
    0,2,3, // Back 8 o clock
    0,3,7, // Back 7 o clock
    0,7,5, // Back 5 o clock
    0,5,4 // Back 3 o clock
  ])
  // prettier-ignore

  geometry.computeVertexNormals()
  return geometry
}

class EnemyShip {
  constructor(radius, position, stop) {
    this.radius = radius
    this.velocity = { x: -0.1, y: 0 }
    this.maxVelocity = { y: 0.1 }
    this.stop = stop
    this.health = 100
    this.cooldown = { shoot: 0, hit: 0 }
    this.type = 'ship'

    let texture = new TextureLoader()
    //Credit to Adobe Stock for the deathstar texture: https://stock.adobe.com/images/scifi-panels-futuristic-texture-spaceship-hull-geometric-pattern-3d-illustration/322065570
    const bodyMaterial = new MeshLambertMaterial({ map: texture.load(ShipTexture) })
    const enemyGeom = createShipBody()
    const enemyMesh = new Mesh(enemyGeom, bodyMaterial)
    const jetRingMaterial = new MeshLambertMaterial({ color: 'red' })
    const jetMaterial = new MeshLambertMaterial({ color: 0xb1b1b1 })
    const jetGeometry = new CylinderGeometry(0.14, 0.14, 1.1, 7)
    const jetFrontGeometry = new CylinderGeometry(0.14, 0.26, 0.6, 8)
    const nobGeo = new SphereGeometry(0.14, 7, 7)
    const jetMesh1 = new Mesh(jetGeometry, jetMaterial)
    const jetFrontMesh = new Mesh(jetFrontGeometry, jetMaterial)
    jetFrontMesh.position.y += 0.7
    jetMesh1.position.x += 0.72
    jetMesh1.position.y -= 0.18
    const jetMesh2 = new Mesh(jetGeometry, jetMaterial)
    jetMesh2.position.x -= 0.72
    jetMesh2.position.y -= 0.18
    const jetRingGeo = new TorusGeometry(0.13, 0.03, 20, 20)
    const jetRingMesh1 = new Mesh(jetRingGeo, jetRingMaterial)
    const jetRingMesh2 = new Mesh(jetRingGeo, jetRingMaterial)
    const nob1 = new Mesh(nobGeo, jetMaterial)
    nob1.position.x += 0.72
    nob1.position.y += 0.367
    const nob2 = new Mesh(nobGeo, jetMaterial)
    nob2.position.x -= 0.72
    nob2.position.y += 0.367
    const nob3 = new Mesh(nobGeo, jetMaterial)
    nob3.position.y += 1
    jetRingMesh1.position.x += 0.72
    jetRingMesh2.position.x -= 0.72
    jetRingMesh1.position.y -= 0.73
    jetRingMesh2.position.y -= 0.73
    jetRingMesh1.rotation.x += Math.PI / 2
    jetRingMesh2.rotation.x += Math.PI / 2

    this.group = new Group()
    this.group.add(enemyMesh)
    this.group.add(jetMesh1)
    this.group.add(jetMesh2)
    this.group.add(jetRingMesh1)
    this.group.add(jetRingMesh2)
    this.group.add(nob1)
    this.group.add(nob2)
    this.group.add(nob3)
    this.group.add(jetRingMesh1)
    this.group.add(jetRingMesh2)
    this.group.add(jetFrontMesh)
    this.group.rotateZ(Math.PI / 2)
    this.group.position.set(position.x, position.y, position.z)
    this.group.scale.set(radius, radius, radius)
  }

  move(position) {
    if (this.group.position.y < position.y) {
      this.velocity.y = Math.min(this.velocity.y + 0.001, this.maxVelocity.y)
    } else if (this.group.position.y > position.y) {
      this.velocity.y = Math.max(this.velocity.y - 0.001, -this.maxVelocity.y)
    }
    this.group.position.x += this.velocity.x
    this.group.position.y += this.velocity.y
    this.group.rotation.x = -this.velocity.y
  }

  updateCooldowns() {
    if (this.cooldown.shoot > 0) {
      this.cooldown.shoot--
    }
    if (this.cooldown.hit > 0) {
      this.cooldown.hit--
      if (this.cooldown.hit === 0) {
        setOpacity(this, 1)
      }
    }
  }
}

export default EnemyShip
